import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { WarningCircle } from "@phosphor-icons/react";
import { unknownVars, normName, isBlank, fmtDateTime } from "./render";

const Radio = ({ checked, onChange, disabled, children, testid }) => (
  <label className={`flex items-start gap-2 text-sm rounded-md border px-3 py-2 cursor-pointer ${checked ? "border-accent/50 bg-accent/[0.05]" : "border-border"} ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}>
    <input type="radio" className="mt-1" checked={checked} disabled={disabled} onChange={onChange} data-testid={testid} />
    <span>{children}</span>
  </label>
);

// Enregistre l'objet + le contenu courant : nouveau template, ou remplacement explicite d'un template existant.
export function SaveTemplateDialog({ open, onClose, subject, body, source, onSaved }) {
  const [list, setList] = useState([]);
  const [mode, setMode] = useState("new");
  const [name, setName] = useState("");
  const [target, setTarget] = useState("");
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    api.get("/admin/leads/templates").then(({ data }) => setList(data.templates)).catch(() => setList([]));
    setMode("new"); setName(source?.name ? `${source.name} (nouvelle version)` : ""); setTarget(source?.id || ""); setAck(false);
  }, [open, source]);
  const t = list.find((x) => x.id === target);
  const dup = mode === "new" && list.some((x) => normName(x.name) === normName(name));
  const unknown = unknownVars(subject, body);
  const blocking = unknown.length > 0 || !subject.trim() || isBlank(body);
  const can = !busy && !blocking && (mode === "new" ? name.trim() && !dup : t && ack);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = mode === "new"
        ? await api.post("/admin/leads/templates", { name, subject, body_html: body })
        : await api.put(`/admin/leads/templates/${t.id}`, { name: t.name, subject, body_html: body, expected_version: t.version });
      toast.success(mode === "new" ? `Template « ${data.name} » enregistré` : `Template « ${data.name} » remplacé (version ${data.version})`);
      onSaved?.(data); onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-surface border-border w-[95vw] max-w-lg max-h-[90vh] overflow-y-auto" data-testid="lead-save-template-dialog">
        <DialogHeader>
          <DialogTitle>Enregistrer comme template</DialogTitle>
          <DialogDescription>Sauvegarde l'objet, le contenu, la mise en forme et les variables de personnalisation de cet email.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Radio checked={mode === "new"} onChange={() => setMode("new")} testid="lead-save-template-mode-new">Créer un nouveau template</Radio>
          {mode === "new" && (
            <div className="pl-6 space-y-1">
              <Input autoFocus value={name} onChange={(e) => setName(e.target.value)} maxLength={80} placeholder="Nom du template (ex. Relance ESN Île-de-France)" className="bg-surface-2 border-border" data-testid="lead-save-template-name" />
              {dup && <p className="text-[11px] text-amber-500" data-testid="lead-save-template-dup">Un template porte déjà ce nom. Choisissez un autre nom, ou « Remplacer un template existant ».</p>}
            </div>
          )}
          <Radio checked={mode === "replace"} disabled={!list.length} onChange={() => setMode("replace")} testid="lead-save-template-mode-replace">Remplacer un template existant</Radio>
          {mode === "replace" && (
            <div className="pl-6 space-y-2">
              <select value={target} onChange={(e) => { setTarget(e.target.value); setAck(false); }} className="w-full bg-surface-2 border border-border rounded-md text-sm px-2 py-2" data-testid="lead-save-template-target">
                <option value="">Choisir le template à remplacer…</option>
                {list.map((x) => <option key={x.id} value={x.id}>{x.name} (v{x.version})</option>)}
              </select>
              {t && (
                <label className="flex items-start gap-2 text-xs text-fg border border-amber-500/40 bg-amber-500/[0.06] rounded-md p-2">
                  <input type="checkbox" className="mt-0.5" checked={ack} onChange={(e) => setAck(e.target.checked)} data-testid="lead-save-template-ack" />
                  <span>Je confirme remplacer l'objet et le contenu de « {t.name} » (version {t.version}, modifiée le {fmtDateTime(t.updated_at)}). L'ancienne version reste conservée dans l'historique.</span>
                </label>
              )}
            </div>
          )}
        </div>
        {unknown.length > 0 && <p className="text-xs text-destructive flex gap-1"><WarningCircle size={14} className="shrink-0" /> Variables inconnues : {unknown.map((v) => `{{${v}}}`).join(", ")}. Corrigez-les avant d'enregistrer.</p>}
        {(!subject.trim() || isBlank(body)) && <p className="text-xs text-destructive">L'objet et le contenu ne doivent pas être vides.</p>}
        <button onClick={save} disabled={!can} className="bg-accent text-black font-semibold rounded-md py-2.5 text-sm disabled:opacity-50" data-testid="lead-save-template-confirm">
          {busy ? "Enregistrement…" : mode === "new" ? "Enregistrer le nouveau template" : "Remplacer le template"}
        </button>
      </DialogContent>
    </Dialog>
  );
}
